import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { getFlashMessages, removeMessage } from 'redux-flash'

import Snackbar from '@material-ui/core/Snackbar'
import SnackbarContent from '@material-ui/core/SnackbarContent'
import IconButton from '@material-ui/core/IconButton'
import InfoIcon from '@material-ui/icons/Info'
import ErrorIcon from '@material-ui/icons/Error'
import CloseIcon from '@material-ui/icons/Close'

import { makeStyles } from '@material-ui/core/styles'

// Kolory komunikatów (decyzja właściciela 2026-09-24). Kontrasty WCAG:
// - tekst #0A0A0A (--ink strony iSA): 19,8:1 na białym, 12,1:1 na #FFC107 (wymóg 4,5:1);
// - ramka informacji w kolorze głównym #CB092A: 5,81:1 wobec białej strony (wymóg 3:1, WCAG 1.4.11);
// - ramka błędu ciemny amber #A16207: 4,92:1 wobec białej strony (wymóg 3:1) i 3,02:1 wobec żółtego tła
//   komunikatu. Sam żółty #FFC107 ma wobec białej strony tylko 1,63:1, więc bez ramki ginął (decyzja 2026-09-24).
// Żółty to standardowy amber #FFC107: w stylach strony nie ma tokenu ostrzeżenia
// (jedyny żółty, --code-amber, koloruje składnię w blokach kodu).
const TEKST = '#0A0A0A'
const TLO_BLEDU = '#FFC107'
const RAMKA_BLEDU = '#A16207'
// SnackbarContent z MUI 4.8 ma padding 6px 16px. Ramkę odejmujemy od paddingu,
// żeby oba komunikaty miały dokładnie ten sam rozmiar i położenie co bez ramki.
const RAMKA = 2

// Komunikaty redux-flash (np. „uzupełnij wymagane pola”). Zastępuje komponent dostawcy
// @bit/…display-flash-toasts 0.0.5, którego rejestr (node.bit.dev) przestał działać.
// Stos komunikatów w rogu ekranu, ikona, przycisk zamknięcia jak w oryginale.
// Informacja: białe tło z czerwoną ramką, błąd: żółte tło z ciemnoamberową ramką; tekst ciemny.
// Komunikat znika sam po czasie z middleware w store.js.
const useStyles = makeStyles((theme) => ({
  stack: {
    display: 'flex',
    position: 'fixed',
    zIndex: 1400,
    flexDirection: 'column-reverse',
    bottom: ({ bottom }) => bottom,
    right: ({ right }) => right,
  },
  item: {
    position: 'static',
    margin: '0.5rem 0',
    maxWidth: 320,
  },
  info: {
    backgroundColor: theme.palette.common.white,
    color: TEKST,
    border: `${RAMKA}px solid ${theme.palette.primary.main}`,
    padding: `${6 - RAMKA}px ${16 - RAMKA}px`,
  },
  error: {
    backgroundColor: TLO_BLEDU,
    color: TEKST,
    border: `${RAMKA}px solid ${RAMKA_BLEDU}`,
    padding: `${6 - RAMKA}px ${16 - RAMKA}px`,
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    // Komunikat o niskiej ocenie ma akapity i listę trenerów w osobnych wierszach.
    whiteSpace: 'pre-line',
  },
  icon: {
    fontSize: 20,
  },
  leadingIcon: {
    opacity: 0.9,
    marginRight: theme.spacing(1),
  },
  infoIcon: {
    color: theme.palette.primary.main,
  },
}))

const FlashToasts = ({ bottom = '1rem', right = '1rem' }) => {
  const classes = useStyles({ bottom, right })
  const messages = useSelector(getFlashMessages) || []
  const dispatch = useDispatch()

  return (
    <div className={classes.stack}>
      {
        messages.map(({ id, message, isError }) => {
          const Icon = isError ? ErrorIcon : InfoIcon
          const messageId = `flash-message-${id}`
          // Komunikat o niskiej ocenie to szablon zaczynający się i kończący znakiem nowej linii;
          // przy pre-line dałoby to pusty pierwszy i ostatni wiersz.
          const text = typeof message === 'string' ? message.trim() : message
          const iconClassName = [classes.icon, classes.leadingIcon, !isError && classes.infoIcon]
            .filter(Boolean)
            .join(' ')

          return (
            // anchorOrigin jak w oryginale: domyślny „bottom-center” z MUI przesuwa toast o -50% szerokości.
            <Snackbar key={id} open={true} className={classes.item} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
              <SnackbarContent
                className={isError ? classes.error : classes.info}
                aria-describedby={messageId}
                message={
                  <span id={messageId} className={classes.message}>
                    <Icon className={iconClassName} />
                    {text}
                  </span>
                }
                action={
                  <IconButton aria-label={'Zamknij komunikat'} color={'inherit'} onClick={() => dispatch(removeMessage(id))}>
                    <CloseIcon className={classes.icon} />
                  </IconButton>
                }
              />
            </Snackbar>
          )
        })
      }
    </div>
  )
}

export default FlashToasts
